<template>
  <div class="network-status-header__wrapper">
    <div class="network-status-header__content pb-0">
      <s-sidebar
        class="network-status-header__sidebar"
        ref="sidebar"
        @on-show="refreshFilter"
      >
        <template #body>
          <network-filter
            ref="networkFilterRef"
            display-fields-in-line
            :is-visible="isSidebarVisible()"
            :show-period-field="false"
            @on-filter-selected="handleFilterSelection"
          />
        </template>
        <template #footer>
          <b-button variant="primary" @click="onSearchButtonClickHandler">
            {{ $t('general.search') }}
          </b-button>
        </template>
      </s-sidebar>
    </div>
    <div v-if="showHeaderItems" class="network-status-header__badges">
      <default-data-header
        :items="headerItems"
        :modal-title="$t('dashboard.system-information')"
      />
    </div>
  </div>
</template>

<script>
import globalEventBus from '@/services/globalEventBus';
import networkStatusOptions from '@/domain/data/generalPanel/networkStatus/networkStatusOptions';
import moduleStatusOptions from '@/domain/data/generalPanel/networkStatus/moduleStatusOptions';
import moduleSelfDiagnosisStatusOptions from '@/domain/data/generalPanel/networkStatus/moduleSelfDiagnosisStatusOptions';
import DefaultDataHeader from '@/components/header/DefaultDataHeader';
import HeaderAssetDataLoaderMixin from '@/components/header/mixin/headerAssetDataLoaderMixin';
import NetworkFilter from '@/domain/components/generalPanel/networkStatus/NetworkFilter';
import networkStatusEvents from '@/domain/events/networkStatus';

export default {
  name: 'NetworkStatusHeader',
  components: {
    DefaultDataHeader,
    NetworkFilter,
  },
  mixins: [HeaderAssetDataLoaderMixin],
  data: () => ({
    filter: {
      companies: [],
      regional: [],
      installations: [],
      assets: [],
      sensors: [],
      moduleStatus: [],
      networkStatus: [],
    },
    completeFilter: {
      companies: [],
      regionals: [],
      installations: [],
      assets: [],
      sensors: [],
      moduleStatus: [],
      networkStatus: [],
    },
  }),
  created() {
    this.loadAllCompleteFilters();
  },
  computed: {
    headerItems() {
      return [
        {
          title: this.$t('header.company'),
          data: this.getItemsIfIsNotAllSelected(
            this.filter.companies,
            this.completeFilter.companies,
          ),
        },
        {
          title: this.$t('header.regional'),
          data: this.getItemsIfIsNotAllSelected(
            this.filter.regional,
            this.completeFilter.regional,
          ),
        },
        {
          title: this.$t('header.installation'),
          data: this.getItemsIfIsNotAllSelected(
            this.filter.installations,
            this.completeFilter.installations,
          ),
        },
        {
          title: this.$t('header.asset'),
          data: this.getItemsIfIsNotAllSelected(
            this.filter.assets,
            this.completeFilter.assets,
          ),
        },
        {
          title: this.$t('header.sensor'),
          data: this.getItemsIfIsNotAllSelected(
            this.filter.sensors,
            this.completeFilter.sensors,
          ),
        },
        {
          title: this.$t('header.sensorStatus'),
          data: this.getItemsIfIsNotAllSelected(
            this.filter.moduleStatus,
            this.completeFilter.moduleStatus,
          ),
        },
        {
          title: this.$t('header.networkStatus'),
          data: this.getItemsIfIsNotAllSelected(
            this.filter.networkStatus,
            this.completeFilter.networkStatus,
          ),
        },
      ];
    },
    showHeaderItems() {
      return (this.headerItems || []).some(({ data }) => !!data.length);
    },
  },
  methods: {
    isSidebarVisible() {
      return this.$refs.sidebar?.$data.show ?? false;
    },
    refreshFilter(shouldCallValidateAndEmit = false) {
      globalEventBus.emit(
        networkStatusEvents.refreshFilter,
        this.filter,
        shouldCallValidateAndEmit,
      );
    },
    async loadAllCompleteFilters() {
      const companies = await this.getCompanies();

      const companyIds = companies.map(({ id }) => id);
      const regional = await this.getRegional(companyIds);

      const regionalIds = regional.map(({ id }) => id);
      const installations = await this.getInstallation(companyIds, regionalIds);

      const installationIds = installations.map(({ id }) => id);
      const assets = await this.getAssets(installationIds);

      const assetIds = assets.map(({ id }) => id);
      const sensors = await this.getSensors(assetIds);

      const moduleStatusLookup = moduleStatusOptions();
      const networkStatusLookup = networkStatusOptions();
      const moduleSelfDiagnosisStatusLookup = moduleSelfDiagnosisStatusOptions();

      this.completeFilter = {
        companies,
        regional,
        installations,
        assets,
        sensors,
        moduleStatus: moduleStatusLookup,
        networkStatus: networkStatusLookup,
        moduleSelfDiagnosisStatus: moduleSelfDiagnosisStatusLookup,
      };
      this.filter = { ...this.completeFilter };
      this.refreshFilter(true);
    },
    onSearchButtonClickHandler() {
      globalEventBus.emit(networkStatusEvents.validateAndEmitFilters);
    },
    handleFilterSelection(filters) {
      this.$emit('apply-filters', filters);
      this.filter = { ...filters.filterObject };
      this.$nextTick(() => {
        this.$refs.sidebar.showSidebar(false);
      });
    },
  },
};
</script>

<style lang="sass" scoped>
.network-status-header
  &__wrapper
    vertical-align: center
    ::v-deep .material-icons-round
      vertical-align: bottom
  &__content
    display: flex
    padding: 25px 40px
    align-items: center
  &__title
    text-transform: uppercase
    flex-grow: 10
  &__sidebar
    display: flex
    vertical-align: bottom
    width: 100%
    justify-content: flex-end
  &__badges
    padding: 1rem 2.7rem 0 2.7rem
</style>
